/**
 * Field descriptions from Confluence "Game fields categorization" (page version 9)
 * https://arkadium.atlassian.net/wiki/spaces/DepartmentProductEngineers/pages/1827930135
 */
export const GAME_FIELD_DESCRIPTIONS = {
  id: 'Primary key for Game API',
  slug: 'Stable game URL used for routing, analytics, and asset paths. Can be aliased per Arena.',
  name: 'Internal catalog name. Arenas prefer localized name for UI display.',
  tier:
    'Declares game priority rating (S>A>B>C>D) based on its success metrics. For catalog reference only.',
  developer: 'Owner studio/developer link. For catalog reference only.',
  metaTemplate: 'Default metadata template',

  renderingType:
    'Determines rendering logic (e.g, iframe index.html vs. embed HTML node directly)',
  sdk: 'SDK product name for catalog/docs (not to be confused with sdkName). For catalog reference only.',
  engine: 'Game engine upon which the game is built. For catalog reference only.',
  engineVersion: 'Game engine version number. For catalog reference only.',
  sdkName:
    'Legacy blob/package path prefix (sdkName or slug) for loading game assets on Legacy Arkadium.com / Arena 5.1.',

  hasAuth: 'Declares authentication/login capability. For catalog reference only.',
  hasEagle: 'Enables Eagle/platform login and related hooks on Arena 5.1 (e.g. gems require Eagle).',

  hasInventory: 'Declares inventory support. For catalog reference only.',
  hasCollection: 'Enables collections tab and UI on Legacy Arkadium.com . Unused on Evo / Arena 5.1.',
  hasLeaderboard: 'Enables leaderboard UI and score submit paths on Evo / Arena 5.1.',
  hasMultiplayer: 'Declares internal multiplayer. For catalog reference only.',
  hasPlatformGameEnd: 'Declares platform game end flow. For catalog reference only.',
  hasCloudSave: 'Declares cloud save capability. For catalog reference only.',
  cloudSaveStorage: 'Location where cloud saves go (e.g. Eagle/platform). For catalog reference only.',

  hasGems:
    'Enables microtransactions/gems for the game on Arena 5.1 (also gated by Eagle / arena gems support).',
  hasInterstitialAds:
    'Declares interstitial ads capability; separate from Ads Free. For catalog reference only.',
  hasRewardedAds: 'Declares rewarded ads capability; separate from Ads Free. For catalog reference only.',
  isAdsFree:
    'Enables ad free experience for the game page. Legacy Arkadium.com skips display+preroll. Arena 5.1 skips preroll. Does not alone block interstitial/rewarded.',
  adPodding:
    'Game-level ad podding flag; Evp uses CMS page adPodding instead — Game API field unused.',
  mtxDetails: 'Game-only notes/URL for shop/MTX. Not overridable per feed.',
  adLayoutCode:
    'Evo monetization density template (e.g. light/medium/heavy/no monetization) for ad rails/widgets.',

  'gamePrerollSettings.isEnabled':
    'Enables game-level preroll config (Evo merges meta.preroll over CMS defaults).',
  'gamePrerollSettings.freePeriod':
    'New-user grace period in days before preroll frequency rules apply. Used in Evo.',
  'gamePrerollSettings.seconds': 'Preroll video duration / timeout in seconds. Used in Evo.',
  'gamePrerollSettings.firstStartRate':
    'Which game-start count first shows preroll (unit = starts, not %). Used in Evo.',
  'gamePrerollSettings.showRate':
    'Show preroll every N starts after firstStartRate. Used in Evo; Arena 5.1 uses a separate theme show-rate.',

  isGRM: 'Enables loading game via Game Rendering Module iframe on Legacy Arkadium.com / Arena 5.1',
  isIframe: 'Prefer iframe embedding of the game package on Legacy Arkadium.com / Arena 5.1',
  isFlex: 'Enables flexible/responsive game container sizing on Legacy Arkadium.com / Arena 5.1',
  hasFullscreen: 'Enables fullscreen control on Evo game page (with feature toggle).',
  hasPortraitMode: 'Declares portrait orientation support;. For catalog reference only.',
  aspectRatio: 'Game frame width/height ratio. Used by Legacy Arkadium.com to size the canvas.',
  iframeAllowOptions:
    'iframe allow= attribute string. Mapped on Arena 5.1 model but not applied in UI today.',

  hasComingSoon: 'Enables Admin Coming Soon. Evo ignores this boolean and uses comingSoonDate + feature flag.',
  comingSoonDate: 'Until this date (future), Evo can show Coming Soon instead of playable game.',
  isSubscriberOnly: 'Gates the game behind Plus subscription on Evo (until date rule passes)',
  isSubscriberOnlyDate:
    'Optional end/start date for subscriber-only gating on Evo (ignored after date passes).',

  'supportInfo.isEnabled': 'Enables support block. Evo shows support if any contact fields are set.',
  'supportInfo.description': 'Third-party support blurb shown on Evo game support UI.',
  'supportInfo.emailText': 'Label for support email on Evo .',
  'supportInfo.email': 'Support email address on Evo .',
  'supportInfo.phoneText': 'Label for support phone on Evo .',
  'supportInfo.phone': 'Support phone number on Evo .',

  liveOpsDependencies: 'Live Ops dependency notes. For catalog reference only.',
  webhookDev: 'Dev environment webhook URL. For catalog reference only.',
  webhookLive: 'Live environment webhook URL. For catalog reference only.',

  // Feed Game–only context (not on Confluence Game table)
  gameVersionId: 'Pinned game version for this feed; empty/Latest uses the live approved version.',
};

/**
 * Fields present on ArenaFeedItemDto (+ nested meta / preroll / supportInfo / rollout).
 * Source: game-api/src/games/dto/arena-feed-item.dto.ts and arena-meta.dto.ts
 * Admin keys mirror DTO names (preroll → gamePrerollSettings.*; version → gameVersionId on Feed Game).
 */
export const GAME_FIELD_FUNCTIONAL = new Set([
  // ArenaFeedItemDto
  'id',
  'name',
  'slug',
  'version',
  'gameVersionId',
  'renderingType',
  'sdkName',
  // Admin selects which meta locale block is published into Arena feed meta
  'metaTemplate',

  // ArenaMetaDto
  'locale',
  'description',
  'rules',
  'alias',
  'htmlTitle',
  'htmlDescription',
  'details',
  'gameReleaseDate',
  'fullTitle',
  'seoTitle',
  'subTitle',
  'backgroundColor',
  'isGRM',
  'hasEagle',
  'hasGems',
  'hasCollection',
  'hasLeaderboard',
  'isAdsFree',
  'hasFullscreen',
  'isFlex',
  'aspectRatio',
  'isIframe',
  'iframeAllowOptions',
  'hasComingSoon',
  'comingSoonDate',
  'isSubscriberOnly',
  'isSubscriberOnlyDate',
  'subscriberOnlyGame',
  'adLayoutCode',
  'adPodding',
  'order',
  'gameSuggestions',
  'categories',
  'primaryCategory',
  'primaryCategorySlug',
  'tags',
  'badges',
  'thumbs',

  // ArenaGamePrerollSettings (meta.preroll)
  'gamePrerollSettings.isEnabled',
  'gamePrerollSettings.freePeriod',
  'gamePrerollSettings.seconds',
  'gamePrerollSettings.firstStartRate',
  'gamePrerollSettings.showRate',

  // ArenaSupportInfoDto (ArenaFeedItemDto.supportInfo)
  'supportInfo.description',
  'supportInfo.emailText',
  'supportInfo.email',
  'supportInfo.phoneText',
  'supportInfo.phone',

  // ArenaFeedRolloutDto
  'rollout.percentage',
  'rollout.segmentation',
]);
